import { debounce } from 'lodash-es';
import { h, render } from 'preact';
import { resolveAll } from '@/core/utils/promises';
import { createId } from '@/core/utils/string';
import type { City } from '@/events/cities/city.schema';
import { ObservabilityStrategy } from '@/observability/observability';
import { SearchResultsView } from './components/SearchResultsView.client';
import { SEARCH_URL } from './search.config';
import type { SearchResults } from './search.schema';
import { getSearchResults } from './search.service';

const SEARCH_DEBOUNCE_DELAY_MS = 300;

type LazyDependencies = {
	getCities: typeof import('@/events/cities/city.service').getCities;
	getClosestCity: typeof import('@/events/cities/city.service').getClosestCity;
	getLocation: typeof import('@/core/data/gateways/api').getLocation;
};

class SearchBarController {
	#apiSeq = 0;
	#debouncedSearch: ReturnType<typeof debounce> | null = null;
	#dropdown: HTMLElement | null = null;
	#input: HTMLInputElement | null = null;
	#inputTouched = false;
	#inputValue: string | undefined = undefined;
	#keystrokeSeq = 0;
	#location: City | undefined;
	#sessionId: string = createId();

	async mount(host: HTMLElement): Promise<void> {
		const form = host.querySelector<HTMLFormElement>('.js-search-form');
		this.#input = host.querySelector<HTMLInputElement>('.js-search-input');
		this.#dropdown = host.querySelector<HTMLElement>('.js-search-dropdown');

		if (!form) {
			ObservabilityStrategy.recordError(
				'Search mount failed: form element not found',
			);

			return;
		}

		if (!this.#input) {
			ObservabilityStrategy.recordError(
				'Search mount failed: input element not found',
			);

			return;
		}

		if (!this.#dropdown) {
			ObservabilityStrategy.recordError(
				'Search mount failed: dropdown element not found',
			);

			return;
		}

		this.#input.autocomplete = 'off';
		this.#input.type = 'search';
		this.#location = undefined;

		await this.#resolveLocation();

		form.addEventListener('submit', this.#handleFormSubmit);
		this.#input.addEventListener('focus', this.#handleInputFocus);
		this.#input.addEventListener('input', this.#handleInput);

		this.#debouncedSearch = debounce(
			this.#handleSearchResults,
			SEARCH_DEBOUNCE_DELAY_MS,
		);
	}

	unmount(host: HTMLElement): void {
		const form = host.querySelector<HTMLFormElement>('.js-search-form');
		this.#input = host.querySelector<HTMLInputElement>('.js-search-input');
		this.#dropdown = host.querySelector<HTMLElement>('.js-search-dropdown');

		if (form) form.removeEventListener('submit', this.#handleFormSubmit);

		if (this.#input) {
			this.#input.removeEventListener('focus', this.#handleInputFocus);
			this.#input.removeEventListener('input', this.#handleInput);
		}

		if (this.#debouncedSearch) this.#debouncedSearch.cancel();
		if (this.#dropdown) render(null, this.#dropdown);
	}

	async #loadDependencies(): Promise<LazyDependencies | undefined> {
		let getCities: LazyDependencies['getCities'] | undefined;
		let getClosestCity: LazyDependencies['getClosestCity'] | undefined;
		let getLocation: LazyDependencies['getLocation'] | undefined;

		try {
			[{ getLocation }, { getCities, getClosestCity }] = await Promise.all([
				import('@/core/data/gateways/api'),
				import('@/events/cities/city.service'),
			]);
		} catch (error) {
			ObservabilityStrategy.recordError(
				error,
				'Failed to load search dependencies',
			);

			return undefined;
		}

		for (const [key, service] of [
			['getCities', getCities],
			// biome-ignore lint/security/noSecrets: Method name
			['getClosestCity', getClosestCity],
			['getLocation', getLocation],
		]) {
			if (typeof service !== 'undefined') continue;

			ObservabilityStrategy.recordError(
				`Search initialization failed, missing service '${key}'`,
			);

			return undefined;
		}

		return { getCities, getClosestCity, getLocation };
	}

	async #resolveLocation(): Promise<void> {
		const dependencies = await this.#loadDependencies();

		if (!dependencies) return;

		let cities: Awaited<ReturnType<LazyDependencies['getCities']>> | null;
		let location: Awaited<ReturnType<LazyDependencies['getLocation']>> | null;

		try {
			[cities, location] = await resolveAll(
				dependencies.getCities(),
				dependencies.getLocation(),
			);
		} catch (error) {
			ObservabilityStrategy.recordError(
				error,
				'Failed to resolve search location',
			);

			return;
		}

		if (!cities) {
			console.debug('Search initialized without cities');

			return;
		}

		if (!location) {
			console.debug('Search initialized without location');

			return;
		}

		const closest = dependencies.getClosestCity(cities ?? [], location);

		if (!closest) {
			console.debug('Could not determine closest city');

			return;
		}

		this.#location = closest;
	}

	// Keep as arrow function, needs lexically bound `this` to avoid `bind()`
	#handleFormSubmit = (event: Event): void => {
		event.preventDefault();
		const query = this.#input?.value.trim();

		if (!query) return;

		const href = `${SEARCH_URL}${encodeURIComponent(query)}`;
		window.location.href = href;
	};

	// Keep as arrow function, needs lexically bound `this` to avoid `bind()`
	#handleInputFocus = (): void => {
		if (this.#inputTouched) {
			void this.#handleSearchResults(this.#inputValue);

			return;
		}

		this.#inputTouched = true;

		if (!this.#input) return;

		this.#keystrokeSeq += 1;
		this.#debouncedSearch?.(this.#input.value.trim());
	};

	// Keep as arrow function, needs lexically bound `this` to avoid `bind()`
	#handleInput = (event: Event): void => {
		if (!(event.target instanceof HTMLInputElement)) return;

		const value = event.target.value.trim();
		this.#keystrokeSeq += 1;
		this.#debouncedSearch?.(value);
	};

	// Keep as arrow function, needs lexically bound `this` to avoid `bind()`
	#handleSearchResults = async (query?: string): Promise<void> => {
		switch (query) {
			case null:
			case undefined:
				return;
		}

		if (!this.#dropdown) return;

		let results: SearchResults = { events: [], performers: [], venues: [] };

		try {
			results = await getSearchResults(query, this.#location);
		} catch (error) {
			ObservabilityStrategy.recordError(error, 'Could not get search results');
		}

		this.#inputValue = query;
		this.#apiSeq += 1;
		this.#renderResults(results);
	};

	#renderResults(results: SearchResults): void {
		if (!this.#dropdown) return;

		render(
			h(SearchResultsView, {
				apiSequence: this.#apiSeq,
				inputValue: this.#inputValue ?? '',
				keystrokeSequence: this.#keystrokeSeq,
				location: this.#location,
				results,
				sessionId: this.#sessionId,
				status: 'ready',
			}),
			this.#dropdown,
		);
	}
}

async function upgrade(element: HTMLElement): Promise<void> {
	const controller = new SearchBarController();
	await controller.mount(element);

	const observeRoot = document.body;
	let observer: MutationObserver | undefined;
	let hasCleanedUp: boolean = false;

	function cleanup(): void {
		if (hasCleanedUp) return;

		hasCleanedUp = true;
		observer?.disconnect();
		controller.unmount(element);
	}

	observer = new MutationObserver(() => {
		if (!element.isConnected) cleanup();
	});

	observer.observe(observeRoot, { childList: true, subtree: true });
}

async function register(): Promise<void> {
	if (typeof window === 'undefined') return;

	const existing = document.querySelectorAll<HTMLElement>('.js-search-bar');
	await Promise.all([...existing].map((element) => upgrade(element)));
}

void register();
